import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { auth, functions } from "../firebase";
import { Button } from "../design/button";
import { Divider } from "../design/divider";
import { ErrorMessage, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Heading } from "../design/heading";
import { Input } from "../design/input";
import { Text } from "../design/text";
import { Link } from "../design/link";
import { Field } from "@headlessui/react";
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

import logo from "../assets/logo.png";

interface PendingInviteInfo {
  accountId: string;
  accountName: string;
  inviteId: string;
  memberType: "staff" | "supervisor";
  invitedByName: string;
  expiresAt: number;
}

interface Errors {
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  general?: string;
}

type Step = "check-email" | "show-invites" | "create-account";

const JoinOrganizationPage: React.FC = () => {
  const navigate = useNavigate();

  // Step state
  const [step, setStep] = useState<Step>("check-email");

  // Check email step
  const [email, setEmail] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<PendingInviteInfo[]>([]);

  // Create account step
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [errors, setErrors] = useState<Errors>({});

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email) {
      setErrors({ email: "Email is required" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }

    setIsChecking(true);

    try {
      const checkPendingInvites = httpsCallable<
        { email: string },
        { invites: PendingInviteInfo[] }
      >(functions, "checkPendingInvites");

      const result = await checkPendingInvites({ email });
      const invites = result.data.invites;

      if (invites.length === 0) {
        setErrors({
          email:
            "No pending invitations found for this email. Please contact your organization administrator.",
        });
      } else {
        setPendingInvites(invites);
        setStep("show-invites");
      }
    } catch (error: any) {
      console.error("Error checking invites:", error);
      setErrors({
        email: error.message || "Failed to check for invitations. Please try again.",
      });
    }

    setIsChecking(false);
  };

  const handleContinueToAccount = () => {
    setStep("create-account");
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate
    const newErrors: Errors = {};
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!password) newErrors.password = "Password is required";
    if (password && password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsCreating(true);

    try {
      const createUserAndAcceptInvites = httpsCallable<
        {
          email: string;
          password: string;
          firstName: string;
          lastName: string;
        },
        {
          success: boolean;
          userId: string;
          accountIds: string[];
          role: "staff" | "supervisor";
          customToken: string;
        }
      >(functions, "createUserAndAcceptInvites");

      const result = await createUserAndAcceptInvites({
        email,
        password,
        firstName,
        lastName,
      });

      // Sign in with custom token
      await signInWithCustomToken(auth, result.data.customToken);

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error creating account:", error);

      if (error.code === "functions/already-exists") {
        setErrors({
          general:
            "An account with this email already exists. Please sign in instead.",
        });
      } else if (error.code === "functions/not-found") {
        setErrors({
          general:
            "No pending invitations found. Please contact your organization administrator.",
        });
      } else {
        setErrors({
          general: error.message || "Failed to create account. Please try again.",
        });
      }
    }

    setIsCreating(false);
  };

  const formatExpiration = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "Expired";
    if (diffDays === 1) return "Expires tomorrow";
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    return `Expires ${date.toLocaleDateString()}`;
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full opacity-20 blur-3xl dark:bg-green-900" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl dark:bg-blue-900" />
      </div>

      <div className="relative w-full max-w-lg px-4">
        <div className="w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50">
          {/* Back Button */}
          <button
            onClick={() => {
              if (step === "check-email") {
                navigate("/signup");
              } else if (step === "show-invites") {
                setStep("check-email");
                setPendingInvites([]);
              } else {
                setStep("show-invites");
              }
            }}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>

          {/* Logo and Header */}
          <div className="text-center mb-8">
            <img src={logo} alt="Visit Proof" className="w-16 h-16 mx-auto mb-4" />
            <Heading className="text-2xl font-bold text-zinc-900 dark:text-white">
              {step === "check-email" && "Join an Organization"}
              {step === "show-invites" && "Your Invitations"}
              {step === "create-account" && "Create Your Account"}
            </Heading>
            <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
              {step === "check-email" &&
                "Enter your email to find pending invitations"}
              {step === "show-invites" &&
                `We found ${pendingInvites.length} invitation${pendingInvites.length > 1 ? "s" : ""} for you`}
              {step === "create-account" &&
                "Complete your profile to accept the invitation"}
            </Text>
          </div>

          {/* Step 1: Check Email */}
          {step === "check-email" && (
            <form onSubmit={handleCheckEmail}>
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
                        <EnvelopeIcon className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                      </div>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="pl-10"
                      />
                    </div>
                    {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                  </Field>
                </FieldGroup>

                <Button
                  type="submit"
                  color="green"
                  className="w-full mt-6"
                  disabled={isChecking}
                >
                  {isChecking ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Checking...
                    </span>
                  ) : (
                    "Check for Invitations"
                  )}
                </Button>
              </Fieldset>
            </form>
          )}

          {/* Step 2: Show Invites */}
          {step === "show-invites" && (
            <div>
              <div className="space-y-3 mb-6">
                {pendingInvites.map((invite) => (
                  <div
                    key={`${invite.accountId}-${invite.inviteId}`}
                    className="p-4 bg-green-50 border border-green-200 rounded-xl dark:bg-green-900/20 dark:border-green-800/50"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-800/50">
                        <BuildingOffice2Icon className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-zinc-900 dark:text-white">
                          {invite.accountName}
                        </p>
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                          Role:{" "}
                          <span className="capitalize font-medium">
                            {invite.memberType}
                          </span>
                        </p>
                        <p className="text-sm text-zinc-500 dark:text-zinc-500">
                          Invited by {invite.invitedByName}
                        </p>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                          {formatExpiration(invite.expiresAt)}
                        </p>
                      </div>
                      <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleContinueToAccount}
                color="green"
                className="w-full"
              >
                Continue to Create Account
              </Button>
            </div>
          )}

          {/* Step 3: Create Account */}
          {step === "create-account" && (
            <form onSubmit={handleCreateAccount}>
              {errors.general && (
                <div className="p-4 mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50">
                  {errors.general}
                </div>
              )}

              <Fieldset>
                <FieldGroup className="space-y-4">
                  {/* Email (read-only) */}
                  <Field>
                    <Label>Email</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
                        <EnvelopeIcon className="w-5 h-5 text-zinc-400" />
                      </div>
                      <Input
                        type="email"
                        value={email}
                        disabled
                        className="pl-10 bg-zinc-100 dark:bg-zinc-800"
                      />
                    </div>
                  </Field>

                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <Field>
                      <Label>First Name</Label>
                      <div className="relative mt-1.5">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
                          <UserIcon className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                        </div>
                        <Input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="John"
                          className="pl-10"
                        />
                      </div>
                      {errors.firstName && (
                        <ErrorMessage>{errors.firstName}</ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Last Name</Label>
                      <Input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        className="mt-1.5"
                      />
                      {errors.lastName && (
                        <ErrorMessage>{errors.lastName}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Password */}
                  <Field>
                    <Label>Password</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 z-10 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <ErrorMessage>{errors.password}</ErrorMessage>
                    )}
                    <p className="text-xs text-zinc-500 mt-1">
                      Must be at least 6 characters
                    </p>
                  </Field>
                </FieldGroup>

                <Divider className="my-6" />

                {/* Summary of what they're joining */}
                <div className="mb-6 p-3 bg-zinc-50 rounded-lg dark:bg-zinc-800/50">
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    You'll be joining:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {pendingInvites.map((invite) => (
                      <li
                        key={`${invite.accountId}-${invite.inviteId}`}
                        className="text-sm font-medium text-zinc-900 dark:text-white flex items-center gap-2"
                      >
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        {invite.accountName} as {invite.memberType}
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  type="submit"
                  color="green"
                  className="w-full"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    "Create Account & Join"
                  )}
                </Button>
              </Fieldset>
            </form>
          )}

          {/* Login Link */}
          <div className="mt-6 text-center">
            <Text className="text-zinc-600 dark:text-zinc-400">
              Already have an account?{" "}
              <Link
                href="/login"
                className="font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400"
              >
                Sign in
              </Link>
            </Text>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
          By creating an account, you agree to our{" "}
          <a
            href="/terms"
            className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default JoinOrganizationPage;
